import { useState } from 'react'
import type {
  BinaryIncentivationDataModel,
  BinaryRewardCalculationInformation,
  ManualInputIncentivationDataModel,
  ManualInputRewardCalculationInformation,
  ScoreIncentivationDataModel,
  ScoreRewardCalculationInformation,
} from '@/api/model'
import { IncentivationDataType } from '@/api/model/incentivationDataType'
import { Input } from '@/components/shadcn/input'
import { Checkbox } from '@/components/shadcn/checkbox'

/** The union the batch-PATCH expects per row (mirrors the old IncentivationInputField). */
export type RewardCalculationInformation =
  | BinaryRewardCalculationInformation
  | ScoreRewardCalculationInformation
  | ManualInputRewardCalculationInformation

/** The instance's current (server) incentivation data — the "original" for dirty checks. */
type IncentivationData =
  | BinaryIncentivationDataModel
  | ScoreIncentivationDataModel
  | ManualInputIncentivationDataModel
  | null
  | undefined

interface Props {
  /** Per-module input type — never mixed per row. */
  dataType: IncentivationDataType
  /** The instance's current incentivation data (the original the draft is compared against). */
  data: IncentivationData
  /** The current draft reward info for this instance, if any (survives pagination/sort). */
  draft: RewardCalculationInformation | undefined
  /**
   * Reports a change: the reward info when the value differs from the original (dirty), or
   * `null` when it equals the original / is empty / invalid (drop the draft). The caller binds
   * the identity (employeeUserId + instanceId).
   */
  onChange: (info: RewardCalculationInformation | null) => void
}

/** Original numeric value (Score → `score`, ManualInput → `enteredReward`). */
function originalNumber(data: IncentivationData, dataType: IncentivationDataType): number | null {
  if (!data) return null
  if (dataType === IncentivationDataType.Score) return (data as ScoreIncentivationDataModel).score ?? null
  if (dataType === IncentivationDataType.ManualInput) return (data as ManualInputIncentivationDataModel).enteredReward ?? null
  return null
}

/** Original binary value (`isFulfilled ?? false`). */
function originalBool(data: IncentivationData): boolean {
  return (data as BinaryIncentivationDataModel | null | undefined)?.isFulfilled ?? false
}

/**
 * Editable sibling of {@link ScoreCell}, used by both the employee table's edit mode and the
 * instance-details pane. Renders the per-module input for the Score value — checkbox (Binary),
 * numeric with decimals (Score) or integers (ManualInput) — and reports dirty changes up.
 * Dirty = value differs from the original; an empty numeric field counts as *no change* (not
 * zeroed), a non-integer ManualInput entry is flagged and registers no draft, and reverting to
 * the original drops the draft. Seeds from an existing draft so edits survive pagination/sort
 * (the row remounts, the initial state reads the draft prop) — key the cell by the instance id
 * where the same cell can host different instances (the details pane) so it re-seeds on change.
 */
export function ScoreEditCell({ dataType, data, draft, onChange }: Props) {
  if (dataType === IncentivationDataType.Binary) {
    return <BinaryEditCell data={data} draft={draft as BinaryRewardCalculationInformation | undefined} onChange={onChange} />
  }
  if (dataType === IncentivationDataType.Score || dataType === IncentivationDataType.ManualInput) {
    return <NumericEditCell data={data} dataType={dataType} draft={draft} onChange={onChange} />
  }
  // None → nothing numeric to edit; render the read-only original.
  return <span className="text-muted-foreground">{originalNumber(data, dataType) ?? '—'}</span>
}

function BinaryEditCell({
  data, draft, onChange,
}: {
  data: IncentivationData
  draft: BinaryRewardCalculationInformation | undefined
  onChange: Props['onChange']
}) {
  const original = originalBool(data)
  const [checked, setChecked] = useState<boolean>(draft?.isFulfilled ?? original)

  function handleChange(next: boolean) {
    setChecked(next)
    onChange(next === original
      ? null
      : { isFulfilled: next, incentivationDataType: IncentivationDataType.Binary })
  }

  return <Checkbox checked={checked} onCheckedChange={(v) => handleChange(v === true)} />
}

function NumericEditCell({
  data, dataType, draft, onChange,
}: {
  data: IncentivationData
  dataType: IncentivationDataType
  draft: RewardCalculationInformation | undefined
  onChange: Props['onChange']
}) {
  const original = originalNumber(data, dataType)
  const draftValue = dataType === IncentivationDataType.Score
    ? (draft as ScoreRewardCalculationInformation | undefined)?.score
    : (draft as ManualInputRewardCalculationInformation | undefined)?.enteredReward
  const [text, setText] = useState<string>(() => {
    if (draft) return draftValue != null ? String(draftValue) : ''
    return original != null ? String(original) : ''
  })
  // ManualInput must be an integer; a non-integer entry flags the field and registers no draft.
  const [invalid, setInvalid] = useState(false)

  function buildInfo(num: number): RewardCalculationInformation {
    return dataType === IncentivationDataType.Score
      ? { score: num, incentivationDataType: IncentivationDataType.Score }
      : { enteredReward: num, incentivationDataType: IncentivationDataType.ManualInput }
  }

  function handleChange(value: string) {
    setText(value)
    const trimmed = value.trim()
    // Empty = no change (deliberately not treated as 0) → drop the draft.
    if (trimmed === '') { setInvalid(false); onChange(null); return }
    const num = Number(trimmed)
    if (!Number.isFinite(num)) { setInvalid(true); onChange(null); return }
    // ManualInput rejects decimals: flag the field, register no draft (not silently truncated).
    if (dataType === IncentivationDataType.ManualInput && !Number.isInteger(num)) {
      setInvalid(true); onChange(null); return
    }
    setInvalid(false)
    // A value equal to the original is not dirty → drop the draft.
    if (num === original) { onChange(null); return }
    onChange(buildInfo(num))
  }

  return (
    <Input
      type="number"
      // Score allows decimals; ManualInput is integer-only (also guarded on change).
      step={dataType === IncentivationDataType.Score ? 'any' : '1'}
      value={text}
      placeholder="—"
      aria-invalid={invalid}
      onChange={(e) => handleChange(e.target.value)}
      className="h-8 w-24"
    />
  )
}
